// React
import React, { Component } from "react";

// Libraries
import Moment from 'moment';
import Numeral from 'numeral';
import { LineChart, Line, ResponsiveContainer, Dot, YAxis, XAxis, ReferenceLine } from 'recharts';

// ErrorHandler
import { ErrorHandler } from '../../Utils';

// Panel
import { PanelHeading, PanelBody, Dropdown } from '../../Components';

// Styles
import "./dashboard.css";

// Stores 
import { UserProfileStore, SampleDataStore, CurrencyStore } from '../../Stores';

/**********************************************************
 * 
 *  Historical pnl shows the users daily pnl
 *  
 **********************************************************/
// Custom Dot for Chart
const CustomDot = (props) => {

    return (<Dot {...props} onClick={props.onClick} onMouseOver={props.onMouseOver}/>);
    
};

class HistoricalPnl extends Component {

/**********************************************************
 *  Constructor
 **********************************************************/
    constructor(props) {

        super(props);

        this.state = {
            data : [],
            selectedDot : {},
            quoteCurrency : 'USD',
            arrowClass : 'icon-arrow-up8 green-text',
            description: ["Cummulative profit and loss numbers from the last x number of days.", "Click on the dots to see your total profit up until that day."],
            loading: false,
            message: null
        };
    }


/**********************************************************
 *  Component Lifecycle Functions
 **********************************************************/

    // After first render, fetch all parameters and the corresponding chart
    componentDidMount() {

        //console.log('HistoricalPnl.componentDidMount()');

        // If user logged in, listen for updates
        // and fetch the hist pnl data
        if(this.props.user !== null) {
            this.listenForUpdates();
            this.getHistPnl();
        }
        else {
            this.getSampleHistPnl();
        }

    }

/**********************************************************
 *  Helper Functions
 **********************************************************/

    // Retrieves and displays the hist pnl
    getHistPnl() {

        //console.log('HistoricalPnl.getHistPnl()');

        const summaries = UserProfileStore.getHistCurrencies();

        if(summaries !== null) {

            const aggregated = this.aggregatePnlToDate(summaries);

            this.setState((prev, props) => {
                return {
                    loading: false,
                    data : aggregated,
                    selectedDot : aggregated[this.state.quoteCurrency][aggregated[this.state.quoteCurrency].length-1]
                }
            });
 
        }

    }


    // Retrieves and displays the sample hist pnl
    getSampleHistPnl() {

        const sampleHistPnl = SampleDataStore.getHistPnlData();
        
        this.setState((prev, props) => {
            return {
                loading : false,
                data : sampleHistPnl,
                selectedDot : sampleHistPnl[this.state.quoteCurrency][sampleHistPnl[this.state.quoteCurrency].length-1]
            }
        });
    }


    // Listen for updates to hist pnl
    listenForUpdates() {
        
        var emitter = UserProfileStore.getEmitter();

        // Listen for pnl updates
        emitter.addListener('histPnl', (updated) => {

            if(updated) {
                this.getHistPnl();
            }
            else {

                const error = UserProfileStore.getHistPnlError();

                var errorMessage = ErrorHandler.historicalPnlError(error);

                this.setState((prev, props) => {
                    return {
                        message : errorMessage,
                        loading : false
                    }
                });
               
            }

        });

        // Listen for loading indicator
        emitter.addListener('loadingHistPnl', (loading) => {

            if(loading) {
                this.setState((prev, props) => {
                    return {
                        loading : true
                    }
                });
            }

        });

    }

    
    // Aggregates the pnl to date
    aggregatePnlToDate(summaries) {

        var formatted = {};

        for(let[currency, dates] of Object.entries(summaries)) {

            var days = [];

            for(let[timestamp, currencies] of Object.entries(dates)) {

                var total = 0;

                for(let[name, value] of Object.entries(currencies)) {

                    total += value;

                }
            
                var dailyPnlObj = {
                    date : timestamp,
                    value : total
                };

                days.push(dailyPnlObj);

            }

            formatted[currency] = days.sort( (a,b) => {
                if(a.date < b.date)
                    return -1;
                if(a.date > b.date)
                    return 1;
                return 0;
            });

        }

        return formatted;

    }

    // Hover over a bar in graph to see its details below
    selectDot = (data, index) => {

        this.setState((prev, props) => {
            return {
                selectedDot : data.payload
            }
        });

    }

    // Selecing a quote currency will update the dropdown,
    // decimal formatting and the currency symbol
    selectQuoteCurrency(currency) {

        this.setState((prev, props) => {
            return {
                quoteCurrency : currency,
                selectedDot : this.state.data[currency][this.state.data[currency].length-1]
            }
        });

    }

    // Given a Y-axis tick, shortform it with letters
    formatTick = (value) => {

        return Numeral(value).format('0a');
    }

/**********************************************************
 *  Render
 **********************************************************/

  render() {

    //console.log('HistoricalPnl.render()');

    var data = this.state.data[this.state.quoteCurrency];

    return (

        <div className="panel panel-flat bg-theme-700">

            <PanelHeading title={"Historical PnL"} description={this.state.description} sample={this.props.user === null ? true : false} 
            hiddenMenu={true} refresh={null}>

                <Dropdown icon='icon-coins' value={this.state.quoteCurrency}>
                    <li onClick={() => this.selectQuoteCurrency('USD')}><a href={null}><div className='currency-symbol'>{CurrencyStore.getCurrencySymbol('USD')}</div>USD</a></li>
                    <li onClick={() => this.selectQuoteCurrency('BTC')}><a href={null}><div className='currency-symbol'>{CurrencyStore.getCurrencySymbol('BTC')}</div>BTC</a></li>
                </Dropdown>

                <Dropdown icon='icon-calendar' value={'Last Week'}>
                    <li><a href={null}>Last Week</a></li>
                    <li><a href={null}>Last Month</a></li>
                </Dropdown>

            </PanelHeading>

            <PanelBody loading={this.state.loading} message={this.state.message}>

                <ResponsiveContainer width="100%" height={175} style={{marginBottom:'20px', fontWeight:'400'}}>

                    <LineChart data={data} margin={{top: -25, right: 5, left: -20, bottom: 30}}>
                        <ReferenceLine y={0} stroke="rgba(255,255,255,0.5)" strokeDasharray="3 3" />
                        <YAxis tick={{stroke: 'rgba(255,255,255,0.8)', strokeWidth: '1px', fontWeight: 'lighter', fontSize: '11px'}} tickFormatter={this.formatTick}/>
                        <Line type='monotone' dataKey='value' stroke='#4FC3F7' strokeWidth={2} dot={<CustomDot onClick={this.selectDot} 
                        onMouseOver={this.selectDot}/>}/>
                    </LineChart>

                </ResponsiveContainer>

                <div className="flex-panel">

                    <div className="child-row">

                        <div className="rox-box left">

                            <div>
                                { Moment(parseInt(this.state.selectedDot.date)).format('MMM DD, YYYY') }
                            </div>

                        </div>

                        <div className="rox-box right">

                            <div>
                                <span className="right-pad">{ CurrencyStore.getCurrencySymbol(this.state.quoteCurrency) }</span>
                                { Numeral(this.state.selectedDot.value).format(CurrencyStore.getFormat(this.state.quoteCurrency)) }
                                <i className={ this.state.selectedDot.value >= 0 ? 'icon-arrow-up8 green-text left-pad arrow' : 'icon-arrow-down8 red-text left-pad arrow'}></i>
                            </div>

                        </div>

                    </div>

            </div>
            
            </PanelBody>

        </div>

    );
  }
}

export default HistoricalPnl;







// WEBPACK FOOTER //
// ./src/Views/Dashboard/HistoricalPnl.js